import { FLIGHT_AIRPORTS } from '../data/flightAirports';

/** Countries available per product line (parent → child). */
export const COUNTRIES_BY_PRODUCT_LINE = {
  Umrah: [{ code: 'SA', label: 'Saudi Arabia' }],
  Domestic: [{ code: 'PK', label: 'Pakistan' }],
  Local: [{ code: 'PK', label: 'Pakistan' }],
  International: [
    { code: 'AE', label: 'United Arab Emirates' },
    { code: 'SA', label: 'Saudi Arabia' },
    { code: 'TR', label: 'Turkey' },
    { code: 'MY', label: 'Malaysia' },
    { code: 'GB', label: 'United Kingdom' },
    { code: 'US', label: 'United States' },
    { code: 'QA', label: 'Qatar' },
    { code: 'OM', label: 'Oman' },
    { code: 'BH', label: 'Bahrain' },
  ],
};

const PK_DEPARTURE_CITIES = [
  'Islamabad',
  'Karachi',
  'Lahore',
  'Peshawar',
  'Quetta',
  'Multan',
  'Faisalabad',
  'Rawalpindi',
  'Sialkot',
];

const CITY_TO_AIRPORT = {
  Islamabad: 'ISB',
  Rawalpindi: 'ISB',
  Lahore: 'LHE',
  Karachi: 'KHI',
  Peshawar: 'PEW',
  Quetta: 'UET',
  Multan: 'MUX',
  Faisalabad: 'LYP',
  Sialkot: 'SKT',
};

const STAY_CITIES_BY_COUNTRY = {
  PK: [
    'Islamabad',
    'Rawalpindi',
    'Lahore',
    'Karachi',
    'Murree',
    'Naran',
    'Hunza',
    'Skardu',
    'Swat',
    'Peshawar',
  ],
  SA: ['Makkah', 'Madina', 'Jeddah'],
  AE: ['Dubai', 'Abu Dhabi', 'Sharjah'],
  TR: ['Istanbul', 'Antalya', 'Konya', 'Bursa'],
  MY: ['Kuala Lumpur'],
  GB: ['London', 'Manchester'],
  US: ['New York'],
  QA: ['Doha'],
  OM: ['Muscat'],
  BH: ['Manama'],
};

const AIRPORT_CODES_BY_COUNTRY = {
  PK: ['ISB', 'LHE', 'KHI', 'PEW', 'LYP', 'MUX', 'SKT', 'UET'],
  SA: ['JED', 'MED', 'RUH'],
  AE: ['DXB', 'AUH', 'SHJ'],
  TR: ['IST', 'SAW', 'AYT'],
  MY: ['KUL'],
  GB: ['LHR', 'MAN'],
  US: ['JFK'],
  QA: ['DOH'],
  OM: ['MCT'],
  BH: ['BAH'],
};

export const getCountriesForProductLine = (productLine) =>
  COUNTRIES_BY_PRODUCT_LINE[productLine] || COUNTRIES_BY_PRODUCT_LINE.International;

export const getDefaultDestinationCountry = (productLine) =>
  getCountriesForProductLine(productLine)[0]?.code || 'AE';

export const getDepartureCities = (productLine) => {
  if (productLine === 'Local') {
    return PK_DEPARTURE_CITIES.filter((c) =>
      ['Islamabad', 'Rawalpindi', 'Lahore', 'Karachi', 'Murree', 'Naran'].includes(c)
    );
  }
  return PK_DEPARTURE_CITIES;
};

export const airportCodeToCity = (code) =>
  FLIGHT_AIRPORTS.find((a) => a.code === code)?.city || code;

export const cityToAirportCode = (city) => CITY_TO_AIRPORT[city] || 'ISB';

export const getAirportsByCodes = (codes) =>
  FLIGHT_AIRPORTS.filter((a) => codes.includes(a.code));

const SA_AIRPORT_LABELS = {
  JED: 'Jeddah / Makkah (JED)',
  MED: 'Madinah (MED)',
  RUH: 'Riyadh (RUH)',
};

const decorateDestinationAirport = (airport, productLine, countryCode) => {
  if (!airport) return airport;
  if (productLine === 'Umrah' || countryCode === 'SA') {
    const label = SA_AIRPORT_LABELS[airport.code];
    if (label) return { ...airport, label };
  }
  if (countryCode === 'TR' && airport.code === 'SAW') {
    return {
      ...airport,
      label: 'Sabiha Gökçen — Istanbul (SAW)',
      city: 'Istanbul (Sabiha)',
    };
  }
  if (countryCode === 'TR' && airport.code === 'IST') {
    return { ...airport, label: 'Istanbul Airport (IST)', city: 'Istanbul' };
  }
  return airport;
};

/** Origins: Pakistan hubs for all trip types (depart from PK). */
export const getOriginAirports = () => getAirportsByCodes(AIRPORT_CODES_BY_COUNTRY.PK);

/** Destinations depend on product line + selected destination country. */
export const getDestinationAirports = (productLine, destinationCountry) => {
  const country = destinationCountry || getDefaultDestinationCountry(productLine);

  let codes;
  if (productLine === 'Umrah') {
    codes = AIRPORT_CODES_BY_COUNTRY.SA;
  } else if (productLine === 'Domestic' || productLine === 'Local') {
    codes = AIRPORT_CODES_BY_COUNTRY.PK;
  } else {
    codes = AIRPORT_CODES_BY_COUNTRY[country] || AIRPORT_CODES_BY_COUNTRY.AE;
  }

  return getAirportsByCodes(codes).map((a) => decorateDestinationAirport(a, productLine, country));
};

export const getStayCitiesForSelection = (productLine, destinationCountry) => {
  const country = destinationCountry || getDefaultDestinationCountry(productLine);
  const list = STAY_CITIES_BY_COUNTRY[country];
  if (list) return list;
  if (productLine === 'Umrah') return STAY_CITIES_BY_COUNTRY.SA;
  if (productLine === 'Domestic' || productLine === 'Local') {
    return STAY_CITIES_BY_COUNTRY.PK;
  }
  return STAY_CITIES_BY_COUNTRY.AE;
};

const pickFirst = (list, current, fallback) => {
  if (current && list.includes(current)) return current;
  if (current && list.find((x) => x.toLowerCase() === String(current).toLowerCase())) return current;
  return list.includes(fallback) ? fallback : list[0];
};

const pickAirport = (airports, current, fallback) => {
  const codes = airports.map((a) => a.code);
  if (current && codes.includes(current)) return current;
  if (fallback && codes.includes(fallback)) return fallback;
  return codes[0];
};

/** Reset dependent fields when parent (product line / country) changes. */
export const coerceBookingLocations = (productLine, data = {}) => {
  const destinationCountry =
    getCountriesForProductLine(productLine).some((c) => c.code === data.destinationCountry)
      ? data.destinationCountry
      : getDefaultDestinationCountry(productLine);

  const origins = getOriginAirports();
  const destinations = getDestinationAirports(productLine, destinationCountry);
  const depCities = getDepartureCities(productLine);
  const stayCities = getStayCitiesForSelection(productLine, destinationCountry);

  const defaults = {
    Umrah: { flyFrom: 'Islamabad', flightOrigin: 'ISB', flightDestination: 'JED' },
    Domestic: { flyFrom: 'Islamabad', flightOrigin: 'ISB', flightDestination: 'ISB' },
    Local: { flyFrom: 'Islamabad', flightOrigin: 'ISB', flightDestination: 'LHE' },
    International: { flyFrom: 'Islamabad', flightOrigin: 'ISB', flightDestination: 'DXB' },
  }[productLine] || { flyFrom: 'Islamabad', flightOrigin: 'ISB', flightDestination: 'DXB' };

  let flightOrigin = pickAirport(origins, data.flightOrigin, defaults.flightOrigin);
  let flightDestination = pickAirport(destinations, data.flightDestination, defaults.flightDestination);

  if (productLine === 'Domestic' || productLine === 'Local') {
    if (flightOrigin === flightDestination && destinations.length > 1) {
      flightDestination = destinations.find((a) => a.code !== flightOrigin)?.code || flightDestination;
    }
  }

  const syncedFlyFrom = airportCodeToCity(flightOrigin) || data.flyFrom;
  const flyFrom = pickFirst(depCities, syncedFlyFrom, defaults.flyFrom);

  const firstEntryLocation = pickFirst(stayCities, data.firstEntryLocation, stayCities[0]);
  const secondEntryLocation = pickFirst(stayCities, data.secondEntryLocation, stayCities[1] || stayCities[0]);
  const thirdEntryLocation = pickFirst(stayCities, data.thirdEntryLocation, stayCities[2] || stayCities[0]);

  return {
    destinationCountry,
    flyFrom,
    flightOrigin,
    flightDestination,
    firstEntryLocation,
    secondEntryLocation,
    thirdEntryLocation,
  };
};

/** Hero / standalone flights: scope tabs map to same rules as product lines. */
export const getFlightScopeOptions = () => [
  { value: 'International', label: 'International' },
  { value: 'Domestic', label: 'Domestic (Pakistan)' },
  { value: 'Umrah', label: 'Umrah' },
];

export const coerceFlightSearchAirports = (scope, origin, destination, destinationCountry) => {
  const country = destinationCountry || getDefaultDestinationCountry(scope);
  const origins = getOriginAirports();
  const destinations = getDestinationAirports(scope, country);
  let o = pickAirport(origins, origin, 'ISB');
  let d = pickAirport(destinations, destination, destinations[0]?.code);
  if (o === d && destinations.length > 1) {
    d = destinations.find((a) => a.code !== o)?.code || d;
  }
  return { origin: o, destination: d, destinationCountry: country };
};
